<template>
  <Dialog
    :model-value="visible"
    title="Remove Program Assignee"
    dialog-type="primary"
    dialog-size="small"
    :close-on-overlay="false"
    @update:model-value="(v) => { if (!v) closeDialog() }"
    @close="closeDialog"
  >
    <div class="content-section">
        <p class="message-text">You are about to remove the following patient.</p>

        <!-- Patient Info Card -->
        <div class="patient-card">
          <div class="patient-info">
            <div class="patient-avatar">
              <span class="avatar-initials">{{ patientInitials }}</span>
            </div>
            <div class="patient-details">
              <p class="patient-name">
                {{ patient.name }} (Accession ID: {{ patient.accessionId || 'N/A' }})
              </p>
              <div class="patient-meta">
                <span class="meta-item">Bio Gender: {{ patient.bioGender || 'N/A' }}</span>
                <span class="meta-divider">|</span>
                <span class="meta-item">DOB: {{ formatDOB(patient.dob) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Refund Toggle (only show if assignee is not completed) -->
        <div v-if="!isAssigneeCompleted" class="refund-control">
          <span class="control-label">Refund this patient?</span>
          <div class="toggle-group">
            <span class="toggle-text">NO</span>
            <ToggleSwitch v-model="refundPatient" />
            <span class="toggle-text">YES</span>
          </div>
        </div>

        <!-- Refund Amount Input (shown when refund is enabled - switch is ON) -->
        <div v-if="!isAssigneeCompleted && refundPatient" class="refund-amount-field">
          <label class="refund-amount-label">Refund Amount</label>
          <div class="refund-amount-input-wrapper">
            <input
              v-model="refundAmount"
              type="text"
              class="refund-amount-input"
              placeholder="e.g. 100"
            />
          </div>
        </div>
    </div>

    <template #footer>
      <ResponsiveButton button_type="outlined" built_in_theme="error" size="medium" @click="closeDialog">Cancel</ResponsiveButton>
      <ResponsiveButton button_type="filled" built_in_theme="dark" size="medium" @click="handleAccept">Accept</ResponsiveButton>
    </template>
  </Dialog>
</template>

<script>
import ToggleSwitch from '@/components/common/ToggleSwitch.vue';
import { notify } from '@/utils/notify';
import { unassignPatientFromWorkflowApi } from '@/api/workflowManage';

export default {
  name: 'RemovePatientConfirmationDialog',
  components: {
    ToggleSwitch
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    patient: {
      type: Object,
      required: true
    },
    programId: {
      type: String,
      required: true
    }
  },
  emits: ['close', 'success'],
  data() {
    return {
      refundPatient: false,
      refundAmount: ''
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.$emit('close');
        }
      }
    },
    patientInitials() {
      if (!this.patient.name) return 'PN';
      const names = this.patient.name.split(' ');
      if (names.length >= 2) {
        return (names[0][0] + names[names.length - 1][0]).toUpperCase();
      }
      return this.patient.name.substring(0, 2).toUpperCase();
    },
    isAssigneeCompleted() {
      return this.patient.status === 'completed';
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        // Reset refund toggle and amount when dialog opens
        this.refundPatient = false;
        this.refundAmount = '';
      }
    }
  },
  methods: {
    closeDialog() {
      this.$emit('close');
    },

    formatDOB(dob) {
      if (!dob) return 'N/A';
      try {
        const date = new Date(dob);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = months[date.getMonth()];
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}-${day}-${year}`;
      } catch (error) {
        return 'N/A';
      }
    },

    async handleAccept() {
      // Validate refund amount if refund is enabled
      if (this.refundPatient && !this.refundAmount) {
        notify({
          title: 'Validation Error',
          message: 'Please enter a refund amount',
          type: 'warning',
          showClose: true
        });
        return;
      }

      try {
        await unassignPatientFromWorkflowApi(this.programId, this.patient.id);

        notify({
          title: 'Success',
          message: `Patient ${this.patient.name} removed from program${this.refundPatient ? ` with $${this.refundAmount} refund` : ''}`,
          type: 'success',
          showClose: true
        });

        this.$emit('success', {
          patientId: this.patient.id,
          refund: this.refundPatient,
          refundAmount: this.refundPatient ? parseFloat(this.refundAmount) : null
        });
        this.closeDialog();
      } catch (error) {
        console.error('Error removing patient:', error);
        notify({
          title: 'Error',
          message: `Failed to remove patient: ${error.response?.data?.message || error.message}`,
          type: 'error',
          showClose: true
        });
      }
    }
  }
};
</script>

<style scoped>
.content-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.message-text {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 28px;
  color: #374957;
  margin: 0;
}

/* Patient Card */
.patient-card {
  background: white;
  border-radius: 10px;
  padding: 16px 24px;
}

.patient-info {
  display: flex;
  gap: 8px;
  padding: 8px;
  border-bottom: 1px solid #c4c4c4;
  padding-bottom: 8px;
}

.patient-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #E5E7EB;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.avatar-initials {
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 500;
  line-height: 22px;
  color: #979797;
  text-align: center;
}

.patient-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
}

.patient-name {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #333;
  margin: 0;
}

.patient-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.meta-item {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #666;
}

.meta-divider {
  color: #666;
  font-size: 12px;
}

/* Refund Control */
.refund-control {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 22px;
  margin-top: 6px;
}

.control-label {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #4a4a4a;
}

.toggle-group {
  display: flex;
  align-items: center;
  gap: 4px;
}

.toggle-text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #333;
}

/* Refund Amount Field */
.refund-amount-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
}

.refund-amount-label {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #4a4a4a;
}

.refund-amount-input-wrapper {
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #c9c9c9;
  border-radius: 4px;
  padding: 8px 12px;
  height: 40px;
}

.refund-amount-input {
  flex: 1;
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #333;
  border: none;
  outline: none;
  padding: 0 8px;
  background: transparent;
}

.refund-amount-input::placeholder {
  color: #999;
}

.refund-amount-input:focus {
  outline: none;
}

.refund-amount-input-wrapper:focus-within {
  border-color: #004879;
}

</style>
